import { Alert, Button, Modal, notification } from "antd";
import { useDispatch, useSelector } from "react-redux";
import React, { useContext, useEffect, useRef, useState } from "react";
import CustomForm from "../CustomForm";
import { userHasLevel } from "../../../engine/AuthRoute";
import { roles, statusPrestationsValues } from "../../../utils/constants";
import { createPortal } from "react-dom";
import { modalsByContext } from "./modals";
import { updateCommentAdminPrestation } from "../../../pages/Prestations/Slices/Prestation.slice";
import { ModalContext } from "../../../engine/context/Modal.provider";
import { UserContext } from "../../../engine/context/User.context";
import { FiCheckCircle } from "react-icons/fi";

export const PrestationEditAdmin = ({ defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);

  useEffect(() => {
    setOpen(defaultOpen);
  }, [defaultOpen]);

  const { prestation, loadingEdit } = useSelector(state => state.prestation);
  const { status } = prestation;
  const { user } = useContext(UserContext);

  // The comment this edit started from, sent with the save: a comment someone else changed in the
  // meantime is not overwritten before the person has seen it (`changedComment`).
  const [previous, setPrevious] = useState(prestation.comment?.admin ?? "");
  const [changedComment, setChangedComment] = useState(null);

  const inputs = prestationCommentAdminInputs(prestation);

  const formRef = useRef(null);

  const context = useContext(ModalContext);
  const dispatch = useDispatch();
  const close = () => {
    setOpen(false);
    context.closeModal(modalsByContext.PrestationEditAdmin);
  };
  const onSubmitForm = async data => {
    const actionResult = await dispatch(
      updateCommentAdminPrestation({
        admin: data?.comment?.admin ?? "",
        previous,
      }),
    );

    if (updateCommentAdminPrestation.fulfilled.match(actionResult)) {
      close();
      return;
    }

    // Nothing was saved: the modal stays open with the typed text. If the comment was changed by
    // someone else, it is shown, and saving again replaces it knowingly.
    if (actionResult.payload?.staleComment) {
      setPrevious(actionResult.payload.comment);
      setChangedComment(actionResult.payload.comment);
    }
  };

  const config = {
    key: prestation._id + modalsByContext.PrestationEditAdmin,
    containerStyle: { minWidth: "500px" },
    title: "Édition commentaire permanence",
    open: open,
    cancelText: "Fermer",
    okText: "Confirmer",
    onOk: () => {
      formRef.current.submit();
    },
    onCancel: close,
    content: (
      <>
        {changedComment !== null && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Le commentaire a été modifié par une autre personne"
            description={
              <>
                <p>
                  Votre texte n'a pas été enregistré, il est conservé
                  ci-dessous. Commentaire enregistré actuellement :
                </p>
                <p style={{ whiteSpace: "pre-wrap", fontStyle: "italic" }}>
                  {changedComment || "(vide)"}
                </p>
                <p style={{ marginBottom: 0 }}>
                  Si vous sauvegardez à nouveau, votre texte remplacera ce
                  commentaire.
                </p>
              </>
            }
          />
        )}
        <CustomForm
          dataSource={inputs}
          handleSubmit={data => onSubmitForm(data)}
          labelCol={24}
          refForm={formRef}
          wrapperCol={24}
          onValuesChange={undefined}
        />
      </>
    ),
    footer: [
      <Button key="close" onClick={close}>
        Fermer
      </Button>,
      <Button
        icon={<FiCheckCircle size={20} />}
        type="primary"
        loading={loadingEdit}
        disabled={loadingEdit}
        onClick={async () => {
          try {
            const test = await formRef.current.validateFields();
            formRef.current.submit();
          } catch (e) {
            notification.error({
              message: e.errorFields.length + " champs est nécessaire",
            });
          }
        }}
      >
        Sauvegarder
      </Button>,
    ],
  };

  return (
    <>
      {createPortal(
        <Modal {...config}>{config.content}</Modal>,
        document.querySelector("#modals"),
      )}
    </>
  );
};

export const prestationCommentAdminInputs = prestation => {
  const {
    intervention: { startDate, endDate, location, extra },
    comment,
  } = prestation;

  return [
    {
      hidden: !userHasLevel({
        only: {
          [roles.admin]: "crud", //
          [roles.permanance]: "crud", //
        },
      }),
      children: [
        {
          label: "Commentaire permanence",
          name: "comment.admin",
          hidden: !userHasLevel({
            only: {
              [roles.admin]: "crud", //
              [roles.permanance]: "crud", //
            },
          }),
          value: comment.admin,
          type: "textarea",
          rows: 4,
          col: 24,
        },
      ],
    },
  ];
};
